import { Controller } from "@hotwired/stimulus"

let selectorIdSequence = 0

// LookupSelector controller for accounting lookup selection in invoice forms
// Renders a searchable dropdown with lookups loaded from AccountingLookup records
// Uses shared lookup-create-modal for creating new lookups
export default class extends Controller {
  static targets = [
    "hiddenInput", "dropdown", "results", "placeholder", "selectButton", "searchInput",
    "noResults", "createButton", "rememberPrompt", "rememberLabel", "clearButton", "clearedInput"
  ]
  static values = {
    lookupType: String,
    perspective: String,
    currentTipas: String,
    memoryKey: String,
    fixedPosition: { type: Boolean, default: true }
  }

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleLookupCreated = this.handleLookupCreated.bind(this)
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('lookup-created', this.handleLookupCreated)
    selectorIdSequence += 1
    this.selectorId = `lookup-selector-${selectorIdSequence}`
    this.initialLookupId = this.hiddenInputTarget.value ? String(this.hiddenInputTarget.value) : ""
    this.element.dataset.lookupSelectorInitialLookupId = this.initialLookupId
    this.updateClearButton()
    this.updateRememberPrompt()
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('lookup-created', this.handleLookupCreated)
  }

  handleLookupCreated(event) {
    const { lookup, lookupType, callerId } = event.detail
    // Only handle events for matching lookup type
    if (lookupType !== this.lookupTypeValue) return

    // Add new option to results for ALL selectors of this type
    this.addNewOptionToResults(lookup)

    // Only auto-select for the selector that initiated the creation
    if (callerId === this.selectorId) {
      this.selectLookup(lookup.id, lookup.kodas, lookup.tipas)
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  toggleDropdown() {
    if (this.dropdownTarget.classList.contains('hidden')) {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ""
        this.filterResults("")
      }
      this.showDropdown()
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      }
    } else {
      this.hideDropdown()
    }
  }

  showDropdown() {
    if (!this.fixedPositionValue) {
      this.dropdownTarget.classList.remove('hidden')
      return
    }

    const triggerRect = this.selectButtonTarget.getBoundingClientRect()
    const dropdownHeight = 320
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const dropdownWidth = this.dropdownWidth(triggerRect.width)
    const dropdownLeft = this.dropdownLeft(triggerRect.left, dropdownWidth)

    // Use fixed positioning to escape overflow containers
    this.dropdownTarget.style.position = 'fixed'
    this.dropdownTarget.style.width = `${dropdownWidth}px`
    this.dropdownTarget.style.left = `${dropdownLeft}px`

    if (spaceBelow < dropdownHeight && triggerRect.top > dropdownHeight) {
      // Open upward
      this.dropdownTarget.style.top = 'auto'
      this.dropdownTarget.style.bottom = `${window.innerHeight - triggerRect.top + 4}px`
    } else {
      // Open downward
      this.dropdownTarget.style.top = `${triggerRect.bottom + 4}px`
      this.dropdownTarget.style.bottom = 'auto'
    }

    this.dropdownTarget.classList.remove('hidden')
  }

  dropdownWidth(triggerWidth) {
    const minWidthByType = {
      preke: 260,
      korespondencija: 240,
      atskaitingas_asmuo: 240,
      saskaitos_tipas: 220,
      projektas: 220
    }
    const minWidth = minWidthByType[this.lookupTypeValue] || 200
    const maxWidth = Math.max(160, window.innerWidth - 16)

    return Math.min(Math.max(triggerWidth, minWidth), maxWidth)
  }

  dropdownLeft(triggerLeft, dropdownWidth) {
    const viewportPadding = 8
    const maxLeft = window.innerWidth - dropdownWidth - viewportPadding

    return Math.max(viewportPadding, Math.min(triggerLeft, maxLeft))
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
    // Reset positioning
    this.dropdownTarget.style.position = ''
    this.dropdownTarget.style.top = ''
    this.dropdownTarget.style.bottom = ''
    this.dropdownTarget.style.left = ''
    this.dropdownTarget.style.width = ''
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    this.filterResults(query)
  }

  filterResults(query) {
    const buttons = this.resultsTarget.querySelectorAll("button[data-lookup-id]")
    let hasMatches = false

    buttons.forEach(button => {
      const kodas = (button.dataset.kodas || "").toLowerCase()
      const pavadinimas = (button.dataset.pavadinimas || "").toLowerCase()
      const displayText = button.textContent.toLowerCase()

      if (kodas.includes(query) || pavadinimas.includes(query) || displayText.includes(query)) {
        button.classList.remove("hidden")
        hasMatches = true
      } else {
        button.classList.add("hidden")
      }
    })

    // Show/hide no results message
    if (this.hasNoResultsTarget) {
      if (!hasMatches && query) {
        this.noResultsTarget.classList.remove("hidden")
      } else {
        this.noResultsTarget.classList.add("hidden")
      }
    }
  }

  select(event) {
    const lookupId = event.currentTarget.dataset.lookupId
    const kodas = event.currentTarget.dataset.kodas
    const tipas = event.currentTarget.dataset.tipas
    // Display only kodas (like ivesk.lt)
    this.selectLookup(lookupId, kodas, tipas)
  }

  selectLookup(lookupId, displayText, tipas = "") {
    // Update display
    this.placeholderTarget.textContent = displayText
    this.placeholderTarget.classList.remove('text-gray-500')
    this.placeholderTarget.classList.add('text-black', 'font-medium')

    // Update hidden input
    if (this.hasClearedInputTarget) this.clearedInputTarget.value = "0"
    this.hiddenInputTarget.value = lookupId
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.currentTipasValue = tipas ? String(tipas) : ""
    this.updateClearButton()
    this.updateRememberPrompt()
    this.dispatchMemoryChange(lookupId, displayText, tipas)

    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.filterResults("")
    }

    this.hideDropdown()
  }

  clear(event) {
    event.stopPropagation()

    // Clear display
    this.placeholderTarget.textContent = "Pasirinkite..."
    this.placeholderTarget.classList.add('text-gray-500')
    this.placeholderTarget.classList.remove('text-black', 'font-medium')

    // Clear hidden input
    if (this.hasClearedInputTarget) this.clearedInputTarget.value = "1"
    this.hiddenInputTarget.value = ""
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.currentTipasValue = ""
    this.updateClearButton()
    this.updateRememberPrompt()
    this.dispatchMemoryChange("", "", "")

    this.hideDropdown()
    this.selectButtonTarget.focus()
  }

  // Open shared create modal
  showCreateForm(event) {
    event.stopPropagation()

    // Get prefill value from search input
    const prefillKodas = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ''

    // Dispatch event to open the shared lookup create modal
    document.dispatchEvent(new CustomEvent('open-lookup-create-modal', {
      detail: {
        lookupType: this.lookupTypeValue,
        prefillKodas: prefillKodas,
        callerId: this.selectorId
      }
    }))

    // Close dropdown
    this.hideDropdown()
  }

  addNewOptionToResults(lookup) {
    // Check if this lookup already exists in results
    const existingButton = this.resultsTarget.querySelector(`button[data-lookup-id="${lookup.id}"]`)
    if (existingButton) return // Already exists, don't add duplicate

    // Remove empty state if present
    const emptyState = this.resultsTarget.querySelector('.text-center.text-gray-500')
    if (emptyState && emptyState.textContent.includes('Nėra')) {
      emptyState.remove()
    }

    // Create wrapper div if not exists
    let wrapper = this.resultsTarget.querySelector('.p-1')
    if (!wrapper) {
      wrapper = document.createElement('div')
      wrapper.className = 'p-1'
      this.resultsTarget.prepend(wrapper)
    }

    // Create new button element
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-50 flex items-center justify-between transition-colors'
    button.dataset.action = 'click->lookup-selector#select'
    button.dataset.lookupId = lookup.id
    button.dataset.kodas = lookup.kodas
    button.dataset.pavadinimas = lookup.pavadinimas || ''
    button.dataset.tipas = lookup.tipas || ''

    let innerHTML = `<div class="flex flex-col min-w-0"><span class="font-medium text-gray-900 truncate">${this.escapeHtml(lookup.kodas)}</span>`
    if (lookup.pavadinimas) {
      innerHTML += `<span class="text-xs text-gray-500 truncate">${this.escapeHtml(lookup.pavadinimas)}</span>`
    }
    innerHTML += '</div>'

    button.innerHTML = innerHTML

    // Insert at the beginning
    wrapper.prepend(button)
  }

  updateRememberPrompt() {
    if (!this.hasRememberPromptTarget) return

    this.rememberPromptTarget.classList.toggle('hidden', !this.hiddenInputTarget.value)
    this.updateRememberLabel()
  }

  updateClearButton() {
    if (!this.hasClearButtonTarget) return

    this.clearButtonTarget.classList.toggle('hidden', !this.hiddenInputTarget.value)
  }

  updateRememberLabel() {
    if (!this.hasRememberLabelTarget) return

    const tipas = String(this.currentTipasValue || "")
    if (this.lookupTypeValue === "preke" && tipas === "1" && this.rememberLabelTarget.dataset.goodsLabel) {
      this.rememberLabelTarget.textContent = this.rememberLabelTarget.dataset.goodsLabel
    } else if (this.lookupTypeValue === "preke" && tipas === "2" && this.rememberLabelTarget.dataset.serviceLabel) {
      this.rememberLabelTarget.textContent = this.rememberLabelTarget.dataset.serviceLabel
    } else if (this.rememberLabelTarget.dataset.fallbackLabel) {
      this.rememberLabelTarget.textContent = this.rememberLabelTarget.dataset.fallbackLabel
    }
  }

  dispatchMemoryChange(lookupId, displayText, tipas) {
    if (!this.hasMemoryKeyValue) return
    const normalizedLookupId = lookupId ? String(lookupId) : ""

    document.dispatchEvent(new CustomEvent("lookup-selector:change", {
      detail: {
        memoryKey: this.memoryKeyValue,
        lookupId: normalizedLookupId,
        displayText: displayText || "",
        tipas: tipas ? String(tipas) : "",
        lookupType: this.lookupTypeValue,
        perspective: this.perspectiveValue,
        touched: normalizedLookupId !== this.initialLookupId
      }
    }))
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
